import { APIError } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { PrimaryButton, SectionMessage } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useInformationRequestsCache, useSubmitItem } from "./hooks";
import { useInformationRequestSaves } from "./InformationRequestSaves";
import { getUnansweredItemIds } from "./utils";

type Props = {
  caseId: string;
  itemId: string;
  isAnswered: boolean;
  /** Whether the server has an answer, as opposed to an unsaved draft. */
  hasSavedAnswer?: boolean;
  /** Saves any unsaved answer; resolves to whether it's saved. */
  saveDraft?: () => Promise<boolean>;
  isMarkedUnanswered: boolean;
  onRequestUpdated: () => Promise<unknown>;
  onUnanswered: () => void;
  onListRefresh: () => Promise<unknown>;
};

export function SubmitItemButton({
  caseId,
  itemId,
  isAnswered,
  hasSavedAnswer = isAnswered,
  saveDraft,
  isMarkedUnanswered,
  onRequestUpdated,
  onUnanswered,
  onListRefresh,
}: Props) {
  const { t } = useTranslation();
  const { waitForSaves } = useInformationRequestSaves();
  const submitItem = useSubmitItem(caseId, itemId);
  const { refreshOutstanding } = useInformationRequestsCache(caseId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (!(await waitForSaves())) {
        setIsSubmitting(false);
        return;
      }
      if (saveDraft && !(await saveDraft())) {
        setIsSubmitting(false);
        return;
      }
      await submitItem();
    } catch (error) {
      const unansweredItemIds = getUnansweredItemIds(error);
      if (unansweredItemIds.includes(itemId)) {
        await onRequestUpdated().catch(() => undefined);
        onUnanswered();
      } else if (error instanceof APIError && error.status === 400) {
        showToast(error.detail, { type: "error" });
        await onListRefresh().catch(() => undefined);
      } else {
        showToast(t("We couldn't submit your answer. Please try again."), {
          type: "error",
        });
      }
      setIsSubmitting(false);
      return;
    }
    setHasSubmitted(true);
    showToast(t("Your answer has been submitted"), { type: "success" });
    await Promise.allSettled([
      onRequestUpdated(),
      onListRefresh(),
      refreshOutstanding(),
    ]);
    setIsSubmitting(false);
  };

  return (
    <div className={styles.wrapper}>
      {isMarkedUnanswered && !hasSavedAnswer && (
        <SectionMessage
          type="danger"
          testID={`information-request-item-${itemId}-unanswered`}
        >
          {t("Answer this request before you submit it.")}
        </SectionMessage>
      )}
      <div className={styles.actions}>
        <PrimaryButton
          size="medium"
          testID={`information-request-item-${itemId}-submit`}
          onPress={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={!isAnswered || hasSubmitted}
        >
          {t("Submit")}
        </PrimaryButton>
      </div>
    </div>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  actions: css({
    display: "flex",
    justifyContent: "flex-end",
  }),
};
